import React, { useState, useRef } from 'react';
import { Upload, X, Image, Loader2, Link, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const ImageUploader = ({ 
  value, 
  onChange, 
  label = "Imagen",
  placeholder = "Arrastra una imagen o haz clic para seleccionar",
  accept = "image/*",
  maxSize = 10 // MB
}) => {
  const { fetchWithAuth } = useAuth();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [useUrl, setUseUrl] = useState(value && value.startsWith('http'));
  const [urlInput, setUrlInput] = useState(value && value.startsWith('http') ? value : '');

  const handleFileSelect = async (file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Por favor selecciona una imagen válida');
      return;
    }

    // Validate file size
    if (file.size > maxSize * 1024 * 1024) {
      setError(`El archivo es muy grande. Máximo ${maxSize}MB`);
      return;
    }

    setError(null);
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetchWithAuth('/api/uploads', {
        method: 'POST',
        body: formData,
        headers: {} // Let browser set Content-Type for FormData
      });

      if (response.ok) {
        const data = await response.json();
        // Construct full URL using the backend URL
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const fullUrl = `${backendUrl}${data.url}`;
        onChange(fullUrl);
        setUseUrl(false);
      } else {
        const err = await response.json();
        setError(err.detail || 'Error al subir la imagen');
      }
    } catch (err) {
      setError('Error de conexión al subir la imagen');
    }

    setUploading(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleInputChange = (e) => {
    const file = e.target.files[0];
    handleFileSelect(file);
  };

  const handleUrlSubmit = () => {
    if (urlInput.trim()) {
      onChange(urlInput.trim());
    }
  };

  const handleClear = () => {
    onChange('');
    setUrlInput('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="block text-sm text-zinc-400">{label}</label>
        <button
          type="button"
          onClick={() => setUseUrl(!useUrl)}
          className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
        >
          {useUrl ? <Upload className="w-3 h-3" /> : <Link className="w-3 h-3" />}
          {useUrl ? 'Subir archivo' : 'Usar URL'}
        </button>
      </div>

      {useUrl ? (
        <div className="flex gap-2">
          <input
            type="url"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="https://ejemplo.com/imagen.jpg"
            className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white focus:outline-none focus:border-emerald-500 text-sm"
          />
          <button
            type="button"
            onClick={handleUrlSubmit}
            className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
          >
            <Check className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div
          onClick={() => !uploading && fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`
            relative border-2 border-dashed rounded-xl p-4 text-center cursor-pointer
            transition-all duration-200
            ${dragOver 
              ? 'border-emerald-500 bg-emerald-500/10' 
              : 'border-zinc-700 hover:border-zinc-600 bg-zinc-800/50'
            }
            ${uploading ? 'pointer-events-none opacity-70' : ''}
          `}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={handleInputChange}
            className="hidden"
          />

          {uploading ? (
            <div className="flex flex-col items-center py-4">
              <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
              <p className="text-sm text-zinc-400 mt-2">Subiendo imagen...</p>
            </div>
          ) : value ? (
            <div className="relative">
              <img 
                src={value} 
                alt="Preview" 
                className="max-h-32 mx-auto rounded-lg object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="hidden flex-col items-center py-4">
                <Image className="w-8 h-8 text-zinc-500" />
                <p className="text-xs text-zinc-500 mt-1">Error al cargar imagen</p>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
                className="absolute -top-2 -right-2 p-1 bg-red-500 hover:bg-red-600 text-white rounded-full"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center py-4">
              <div className={`p-3 rounded-full mb-2 ${dragOver ? 'bg-emerald-500/20' : 'bg-zinc-700'}`}>
                <Upload className={`w-6 h-6 ${dragOver ? 'text-emerald-400' : 'text-zinc-400'}`} />
              </div>
              <p className="text-sm text-zinc-400">{placeholder}</p>
              <p className="text-xs text-zinc-500 mt-1">PNG, JPG, WEBP hasta {maxSize}MB</p>
            </div>
          )}
        </div>
      )}

      {error && (
        <p className="text-sm text-red-400 flex items-center gap-1">
          <X className="w-4 h-4" />
          {error}
        </p>
      )}
    </div>
  );
};

export default ImageUploader;
